import React from 'react'
import { useMsal, useAccount } from '@azure/msal-react'
import { apiRequest } from '../utils/apiClient'

export type Env = 'dev' | 'prod'

export interface AppEnvInfo {
  env: Env
  hasFe: boolean
  hasBe: boolean
}

// Mirrors the server's /api/apps (qa-report-portal-server/src/dashboardConfig.ts is the source of truth)
export interface AppInfo {
  key: string
  label: string
  tier: 'core' | 'additional'
  reportPrefix: string
  envs: AppEnvInfo[]
  hasCi: boolean
  // Third-party service: sub-tab only, no card on the Additional Services overview
  external: boolean
}

interface AppsState {
  apps: AppInfo[]
  loading: boolean
  error: string | null
}

const AppsContext = React.createContext<AppsState>({ apps: [], loading: true, error: null })

export const AppsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance } = useMsal()
  const account = useAccount()
  const [state, setState] = React.useState<AppsState>({ apps: [], loading: true, error: null })

  React.useEffect(() => {
    if (!account) return
    let cancelled = false

    // Retried: the nav and app routes are built from this list, so one failed call would leave
    // them missing until a reload
    const load = async (attempt = 1): Promise<void> => {
      try {
        const data = await apiRequest<{ apps: AppInfo[] }>(instance, account, 'get', '/api/apps')
        if (!cancelled) setState({ apps: data.apps, loading: false, error: null })
      } catch (err) {
        console.error(`Error loading app list (attempt ${attempt}):`, err)
        if (cancelled) return
        if (attempt < 3) {
          setTimeout(() => void load(attempt + 1), 2000 * attempt)
        } else {
          setState({ apps: [], loading: false, error: 'Failed to load app list' })
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [account, instance])

  return <AppsContext.Provider value={state}>{children}</AppsContext.Provider>
}

export const useApps = (): AppsState => React.useContext(AppsContext)
